import React, { useEffect, useRef, useState } from 'react';
import 'intersection-observer';

import { StyledFixedHeightContainer, StyledHiddenDiv, StyledPage, StyledPageContent } from './StyledComponents';

import type { ReactNode } from 'react';

type Props = {
  options?: {
    root: Element | null;
    rootMargin: string;
    threshold: Array<number>;
  };
  backgroundColor?: string;
  borderBottomColor?: string;
  children: ReactNode;
  passDownFixedStatus?: boolean;
  offsetTop?: number;
  offsetForLeftNav?: boolean;
  fixedOuterContainerStyles?: { height: number; boxSizing?: string } | null;
  isLoading?: boolean;
};

const StickyContainer = ({
  // These are the actual defaults for the IntersectionObserver API
  // including them here for clarity
  options = {
    root: null, // null defaults to the viewport
    rootMargin: '0px 0px 0px 0px', // offsets for one or more sides of the root's bounding box
    threshold: [0.0], // can use single number or a range between 0.0-1.0
  },
  backgroundColor = 'white',
  borderBottomColor,
  children,
  passDownFixedStatus = false,
  offsetTop = 0,
  offsetForLeftNav = false,
  fixedOuterContainerStyles = null,
  isLoading = false,
}: Props) => {
  const [isFixedPosition, setIsFixedPosition] = useState(false);
  const scrollRef = useRef(null);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Don't set fixed position if we're in loading state
    if (isLoading) {
      setIsFixedPosition(false);
      return;
    }

    observer.current = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
        setIsFixedPosition(true);
      } else {
        setIsFixedPosition(false);
      }
    }, options);

    if (scrollRef.current) {
      observer.current.observe(scrollRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [isLoading]);

  const renderChildren = () => {
    return passDownFixedStatus
      ? React.Children.map(children, (child) => {
          return React.cloneElement(child as React.ReactElement<{ isFixedPosition: boolean }>, { isFixedPosition });
        })
      : children;
  };

  const renderContent = () => {
    return (
      <StyledPage
        isFixedPosition={isFixedPosition}
        offsetTop={offsetTop}
        offsetForLeftNav={offsetForLeftNav}
        fluid={true}
        backgroundColor={isFixedPosition === true && backgroundColor}
        borderBottomColor={isFixedPosition === true && borderBottomColor}
      >
        <StyledPageContent data-testid="StickyContainer-PageContent" fluid={false}>
          {renderChildren()}
        </StyledPageContent>
      </StyledPage>
    );
  };

  return (
    <div data-testid="StickyContainer">
      <StyledHiddenDiv ref={scrollRef} />
      {fixedOuterContainerStyles !== null ? (
        <StyledFixedHeightContainer
          offsetForLeftNav={offsetForLeftNav}
          borderBottomColor={isFixedPosition === true && borderBottomColor}
          backgroundColor={isFixedPosition === true && backgroundColor}
          isFixedPosition={isFixedPosition}
          styles={isFixedPosition === true && fixedOuterContainerStyles}
          offsetTop={offsetTop}
        >
          {renderContent()}
        </StyledFixedHeightContainer>
      ) : (
        renderContent()
      )}
    </div>
  );
};

export default StickyContainer;
